import {
  ComponentProps,
  ForwardedRef,
  forwardRef,
  useCallback,
  useState,
} from "react";
import { usePlacesAutocomplete } from "@app/hooks/use-places-autocomplete";
import { InputBase } from "@app/lib/ui-foundation";
import "./places-autocomplete-input.css";

type PlacesAutocompleteInputProps = ComponentProps<typeof InputBase> & {
  onPlaceSelect?: (selection: {
    address: string;
    place: google.maps.places.PlaceResult;
  }) => void;
  autocompleteOptions?: google.maps.places.AutocompleteOptions;
  enableAutocomplete?: boolean;
};

function assignRef<T>(ref: ForwardedRef<T>, value: T | null): void {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  ref.current = value;
}

export const PlacesAutocompleteInput = forwardRef<
  HTMLInputElement,
  PlacesAutocompleteInputProps
>(
  (
    {
      onPlaceSelect,
      autocompleteOptions,
      enableAutocomplete = true,
      ...inputProps
    },
    forwardedRef,
  ) => {
    const [inputElement, setInputElement] = useState<HTMLInputElement | null>(
      null,
    );

    const handleInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        setInputElement(node);
        assignRef(forwardedRef, node);
      },
      [forwardedRef],
    );

    usePlacesAutocomplete({
      inputElement,
      enabled: enableAutocomplete,
      autocompleteOptions,
      onPlaceSelect: ({ place, inputValue }) => {
        const address =
          place.formatted_address?.trim() ||
          inputValue.trim() ||
          place.name?.trim();
        if (!address) {
          return;
        }

        onPlaceSelect?.({
          address,
          place,
        });
      },
    });

    return <InputBase {...inputProps} ref={handleInputRef} />;
  },
);

PlacesAutocompleteInput.displayName = "PlacesAutocompleteInput";
